import angular from 'angular';
import type { UserSession } from '@b2b/common/services/auth/types';
import type { FeatureFlagBridge } from './plugins/feature-flags/feature-flag-bridge';

export interface AngularJsInjector {
  get<T>(token: string): T;
}

/**
 * Pure function. Accepts a resolved AngularJS injector and a FeatureFlagBridge;
 * connects the bridge to ThemesHelperService, monkey-patches the service so
 * AngularJS controllers also see overrides, and returns the auth API methods.
 *
 * No globals touched. Throws if the injector cannot resolve required services —
 * the caller (initDevTools) owns the try/catch.
 */
export function buildAngularJsBridgeApi(
  injector: AngularJsInjector,
  featureFlagBridge: FeatureFlagBridge,
): {
  getUserFromCookie: () => UserSession | null;
  getCSRUserFromCookie: () => UserSession | null;
} {
  const themesHelperService = injector.get<any>('ThemesHelperService');
  const authenticationService = injector.get<any>('AuthenticationService');

  // Capture original BEFORE connecting the bridge. The bridge must delegate to
  // the original function, not the monkey-patched service — otherwise getFlag()
  // calls themesHelperService.getFeatureFlag() which calls getFlag() again (loop).
  const original = themesHelperService.getFeatureFlag.bind(themesHelperService);

  featureFlagBridge.connect({ getFeatureFlag: original });

  // Re-patch the service instance so AngularJS controllers that call
  // ThemesHelperService.getFeatureFlag directly also see overrides.
  themesHelperService.getFeatureFlag = (key: string) =>
    featureFlagBridge.getFlag(key) ?? original(key);

  return {
    getUserFromCookie: () => authenticationService.getUserFromCookie(),
    getCSRUserFromCookie: () => authenticationService.getCSRUserFromCookie(),
  };
}

/**
 * Wires the document.ready callback that resolves the AngularJS injector and
 * calls onReady with it. Separated from buildAngularJsBridgeApi so that tests
 * of the bridge logic never need angular.element.
 */
export function registerAngularJsBridge(onReady: (injector: AngularJsInjector) => void): void {
  angular.element(document).ready(() => {
    try {
      const injector = angular.element(document.body).injector();
      if (injector) {
        onReady(injector);
      }
    } catch {
      // Services may not be available in all contexts
    }
  });
}
